import { useEffect, useState } from 'react';
import { SubscriptionType } from '../../generated/api/models.js';
import { AddOnRulesSubscriptionType } from '../enums.js';
import { fetchAddOnRules } from '../fetch.js';
import { emptyAddOnRulesResponse } from '../utils/addOnRulesUtils.js';
import type { AddOnRulesResponse } from '../../generated/api/addOnRulesResponse.js';
import type {
  AssociationRecord,
  DependencyRecord,
} from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';

export interface AddOnAssociationResult {
  addOnRulesLoading: boolean;
  associations: AssociationRecord[];
  dependencies: DependencyRecord[];
}

export interface UseAddOnRulesOptions {
  loadNettiRules?: boolean;
  loadSubscriptionRules?: boolean;
  loadMobilePbxRules?: boolean;
}

const rulesCache = new Map<string, Promise<AddOnRulesResponse>>();

const cachedFetchAddOnRules = (subscriptionType?: string): Promise<AddOnRulesResponse> => {
  const key = subscriptionType ?? 'DEFAULT';
  const existing = rulesCache.get(key);
  if (existing) {
    return existing;
  }
  const pending = fetchAddOnRules(subscriptionType).then(
    response => {
      if (!response.associations?.[0]) {
        rulesCache.delete(key);
      }
      return response;
    },
    error => {
      rulesCache.delete(key);
      throw error;
    }
  );
  rulesCache.set(key, pending);
  return pending;
};

export const useAddOnRules = (
  loadNettiRulesOrOptions?: boolean | UseAddOnRulesOptions,
  loadSubscriptionRules: boolean = true
): AddOnAssociationResult => {
  // Support both old signature and new options object
  const options: UseAddOnRulesOptions =
    typeof loadNettiRulesOrOptions === 'object'
      ? loadNettiRulesOrOptions
      : {
          loadNettiRules: loadNettiRulesOrOptions,
          loadSubscriptionRules,
          loadMobilePbxRules: loadSubscriptionRules, // Default to same as loadSubscriptionRules for backwards compatibility
        };

  const {
    loadNettiRules = false,
    loadSubscriptionRules: shouldLoadSubscriptionRules = true,
    loadMobilePbxRules = shouldLoadSubscriptionRules,
  } = options;

  const [subscriptionAddOnRules, setSubscriptionAddOnRules] = useState<AddOnRulesResponse>();
  const [mobilePbxAddOnRules, setMobilePbxAddOnRules] = useState<AddOnRulesResponse>();
  const [nettiAddOnRules, setNettiAddOnRules] = useState<AddOnRulesResponse>();
  const [nettiFourGAddOnRules, setNettiFourGAddOnRules] = useState<AddOnRulesResponse>();

  useEffect(() => {
    if (shouldLoadSubscriptionRules && !subscriptionAddOnRules) {
      cachedFetchAddOnRules()
        .then(setSubscriptionAddOnRules)
        .catch((_e: Error) => {
          setSubscriptionAddOnRules({ ...emptyAddOnRulesResponse });
        });
    }
  }, [shouldLoadSubscriptionRules, subscriptionAddOnRules]);

  useEffect(() => {
    if (loadMobilePbxRules && !mobilePbxAddOnRules) {
      cachedFetchAddOnRules(SubscriptionType.MOBILE_PBX)
        .then(setMobilePbxAddOnRules)
        .catch((_e: Error) => {
          setMobilePbxAddOnRules({ ...emptyAddOnRulesResponse });
        });
    }
  }, [loadMobilePbxRules, mobilePbxAddOnRules]);

  useEffect(() => {
    if (loadNettiRules && !nettiAddOnRules) {
      cachedFetchAddOnRules(AddOnRulesSubscriptionType.ELISA_NETTI)
        .then(setNettiAddOnRules)
        .catch((_e: Error) => {
          setNettiAddOnRules({ ...emptyAddOnRulesResponse });
        });
    }
  }, [loadNettiRules, nettiAddOnRules]);

  useEffect(() => {
    if (loadNettiRules && !nettiFourGAddOnRules) {
      cachedFetchAddOnRules(AddOnRulesSubscriptionType.ELISA_NETTI_4G)
        .then(setNettiFourGAddOnRules)
        .catch((_e: Error) => {
          setNettiFourGAddOnRules({ ...emptyAddOnRulesResponse });
        });
    }
  }, [loadNettiRules, nettiFourGAddOnRules]);

  const elisaNettiRulesLoading = loadNettiRules && (!nettiAddOnRules || !nettiFourGAddOnRules);

  const associations: AssociationRecord[] = [
    ...(shouldLoadSubscriptionRules ? (subscriptionAddOnRules?.associations[0] as AssociationRecord[]) || [] : []),
    ...(loadMobilePbxRules ? (mobilePbxAddOnRules?.associations[0] as AssociationRecord[]) || [] : []),
    ...((nettiAddOnRules?.associations[0] as AssociationRecord[]) || []),
    ...((nettiFourGAddOnRules?.associations[0] as AssociationRecord[]) || []),
  ];

  const dependencies: DependencyRecord[] = [
    ...(shouldLoadSubscriptionRules ? (subscriptionAddOnRules?.dependencies[0] as DependencyRecord[]) || [] : []),
    ...(loadMobilePbxRules ? (mobilePbxAddOnRules?.dependencies[0] as DependencyRecord[]) || [] : []),
    ...((nettiAddOnRules?.dependencies[0] as DependencyRecord[]) || []),
    ...((nettiFourGAddOnRules?.dependencies[0] as DependencyRecord[]) || []),
  ];

  const subscriptionRulesLoading = shouldLoadSubscriptionRules && !subscriptionAddOnRules;
  const mobilePbxRulesLoading = loadMobilePbxRules && !mobilePbxAddOnRules;

  return {
    addOnRulesLoading: elisaNettiRulesLoading || subscriptionRulesLoading || mobilePbxRulesLoading,
    associations,
    dependencies,
  };
};
